import {
  Aptos,
  AptosConfig,
  Network,
  Account,
  Ed25519PrivateKey,
  Ed25519PublicKey,
  Ed25519Signature,
} from "@aptos-labs/ts-sdk";
import bs58 from "bs58";

// Export all types
export * from "./types";

export interface A402Config {
  network: "testnet" | "mainnet";
}

export interface A402PaymentInfo {
  user: {
    address: string;
    account: Account;
  };
  payment: {
    txHash: string;
    resourceId: string;
    amount: number;
    currency: string;
  };
}

// Augment global Request interface
declare global {
  namespace Express {
    interface Request {
      a402?: A402PaymentInfo;
    }
  }
}
export interface A402Request extends Request {
  a402: {
    user: {
      address: string;
      account: Account;
    };
    payment: {
      txHash: string;
      resourceId: string;
      amount: number;
      currency: string;
    };
  };
}

export class A402SDK {
  private aptos: Aptos;
  private config: A402Config;
  private networkConfig: {
    aptosNetwork: Network;
    contractAddress: string;
    frontendUrl: string;
  };

  constructor(config: A402Config) {
    this.config = config;

    // Internal network configuration
    this.networkConfig = this.getNetworkConfig(config);

    console.log("[A402 SDK] Initializing with network:", config.network);
    console.log("[A402 SDK] Frontend URL:", this.networkConfig.frontendUrl);

    // Initialize Aptos client
    const aptosConfig = new AptosConfig({
      network: this.networkConfig.aptosNetwork,
    });
    this.aptos = new Aptos(aptosConfig);

    console.log("[A402 SDK] Initialization complete");
  }

  private getNetworkConfig(config: A402Config) {
    const configs = {
      testnet: {
        aptosNetwork: Network.TESTNET,
        contractAddress:
          process.env.A402_TESTNET_CONTRACT ||
          "0xd75ad93150725ce1139000d075a61d3be5c464b0b96be6fce193649c0ec3854a",
        frontendUrl: "https://a402.vercel.app",
      },
      mainnet: {
        aptosNetwork: Network.MAINNET,
        contractAddress: process.env.A402_MAINNET_CONTRACT || "0x...",
        frontendUrl: "https://a402.vercel.app",
      },
    };

    return configs[config.network];
  }

  /**
   * Main middleware function for protecting endpoints
   */
  /**
   * Main middleware function for protecting endpoints
   */
  protect() {
    return (req: any, res: any, next: any): Promise<void> => {
      return this.handleMiddleware(req, res, next);
    };
  }

  private async handleMiddleware(req: any, res: any, next: any): Promise<void> {
    const startTime = Date.now();
    try {
      console.log("\n========== A402 MIDDLEWARE START ==========");
      console.log(`[A402] Request: ${req.method} ${req.originalUrl}`);

      // Step 1: Get endpoint info from URL and method
      const endpointInfo = await this.getEndpointInfoFromRequest(req);
      if (!endpointInfo) {
        console.log("[A402] ❌ No endpoint found for this URL");
        res.status(404).json({
          error: "Endpoint Not Found",
          message: "This endpoint is not registered as a payable resource",
        });
        return;
      }

      console.log(`[A402] ✅ Endpoint found:`);
      console.log(`  - Endpoint ID: ${endpointInfo.id}`);
      console.log(`  - Resource: ${endpointInfo.resource.name}`);
      console.log(
        `  - Price: ${endpointInfo.price_per_call} ${endpointInfo.currency}`
      );

      // Step 2: Extract API key from headers
      const apiKey = this.extractApiKey(req);
      if (!apiKey) {
        console.log("[A402] ❌ No API key provided");
        this.sendPaymentRequiredResponse(res, endpointInfo);
        return;
      }
      console.log(`[A402] ✅ API key found: ${apiKey.substring(0, 10)}...`);

      // Step 3: Validate access token and get buyer info
      const tokenData = await this.validateAccessToken(
        apiKey,
        endpointInfo.resource_id,
        endpointInfo.id
      );
      if (!tokenData) {
        console.log("[A402] ❌ Invalid or expired access token");
        res.status(401).json({
          error: "Invalid API Key",
          message:
            "The provided API key is invalid, expired, or not authorized for this endpoint",
        });
        return;
      }

      console.log(`[A402] ✅ Access token valid:`);
      console.log(`  - Buyer: ${tokenData.buyer_address}`);

      // Step 4: Get buyer's account from buyers table
      const buyerAccount = await this.getBuyerAccount(tokenData.buyer_address);
      if (!buyerAccount) {
        console.log("[A402] ❌ Buyer account not found");
        res.status(401).json({
          error: "Account Not Found",
          message:
            "No payment account found for this API key. Please regenerate your API key.",
        });
        return;
      }

      console.log(`[A402] ✅ Buyer account found:`);
      console.log(`  - Wallet: ${buyerAccount.wallet_address}`);
      console.log(`  - Server address: ${buyerAccount.account.accountAddress}`);

      // Step 5: Check balance before payment
      const balance = await this.aptos.getAccountAPTAmount({
        accountAddress: buyerAccount.account.accountAddress,
      });
      const balanceAPT = balance / 100000000;
      console.log(`[A402] 💰 Current balance: ${balanceAPT} APT`);

      if (balanceAPT < endpointInfo.price_per_call) {
        console.log(`[A402] ❌ Insufficient balance`);
        res.status(402).json({
          error: "Insufficient Balance",
          message: `Required: ${endpointInfo.price_per_call} APT, Available: ${balanceAPT} APT`,
          required: endpointInfo.price_per_call,
          available: balanceAPT,
          currency: "APT",
        });
        return;
      }

      // Step 6: Make payment via smart contract
      console.log("[A402] 🔄 Initiating payment...");
      const payment = await this.makePaymentForEndpoint(
        buyerAccount.account,
        endpointInfo,
        tokenData.buyer_address
      );

      console.log(`[A402] ✅ Payment successful:`);
      console.log(`  - TX Hash: ${payment.txHash}`);
      console.log(`  - Amount: ${payment.amount} ${payment.currency}`);

      // Step 7: Attach payment info to request and continue
      req.a402 = {
        user: {
          address: tokenData.buyer_address,
          account: buyerAccount.account,
        },
        payment: {
          txHash: payment.txHash,
          resourceId: endpointInfo.resource_id,
          amount: endpointInfo.price_per_call,
          currency: endpointInfo.currency,
        },
      };

      console.log(
        `[A402] ✅ Middleware complete in ${Date.now() - startTime}ms`
      );
      console.log("========== A402 MIDDLEWARE END ==========\n");
      next();
    } catch (error: any) {
      console.error("[A402] ❌ Middleware error:", error);
      console.log("========== A402 MIDDLEWARE ERROR ==========\n");

      res.status(500).json({
        error: "Payment Processing Error",
        message: error.message,
        details:
          process.env.NODE_ENV === "development" ? error.stack : undefined,
      });
    }
  }

  private extractApiKey(req: any): string | null {
    const authHeader = req.headers.authorization;
    const apiKeyHeader = req.headers["a402-api-key"] as string;
    const xApiKeyHeader = req.headers["x-api-key"] as string;

    if (authHeader && authHeader.startsWith("A402 ")) {
      return authHeader.substring(5);
    }

    if (apiKeyHeader) {
      return apiKeyHeader;
    }

    if (xApiKeyHeader) {
      return xApiKeyHeader;
    }

    return null;
  }

  private async getEndpointInfoFromRequest(req: any): Promise<any | null> {
    try {
      const path = req.path || req.url.split("?")[0];
      const method = req.method.toUpperCase();

      console.log(`[A402] Looking for endpoint: ${method} ${path}`);

      const response = await fetch(
        `${
          this.networkConfig.frontendUrl
        }/api/sdk/endpoint?path=${encodeURIComponent(
          path
        )}&method=${encodeURIComponent(method)}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`[A402] No endpoint found for ${method} ${path}`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data) {
        console.log(`[A402] No data found for ${method} ${path}`);
        return null;
      }

      return data;
    } catch (error) {
      console.error("[A402] Error fetching endpoint:", error);
      return null;
    }
  }

  private sendPaymentRequiredResponse(res: any, endpointInfo: any): void {
    res.status(402).json({
      error: "Payment Required",
      message: `Access to this endpoint requires payment of ${endpointInfo.price_per_call} ${endpointInfo.currency}`,
      endpoint: {
        id: endpointInfo.id,
        path: endpointInfo.path,
        method: endpointInfo.method,
        description: endpointInfo.description,
      },
      resource: {
        id: endpointInfo.resource_id,
        name: endpointInfo.resource.name,
      },
      payment: {
        amount: endpointInfo.price_per_call,
        currency: endpointInfo.currency,
        network: this.config.network,
      },
      instructions: {
        step1:
          "Generate an API key by signing the resource ID with your wallet",
        step2: "Add header: Authorization: A402 <your-api-key>",
        step3: "Ensure your account has sufficient balance",
      },
    });
  }

  /**
   * Get buyer's payment balance
   */
  async getBuyerBalance(buyerAddress: string): Promise<{
    balance: number;
    currency: string;
    backendAddress: string;
  }> {
    console.log(`[A402] Checking balance for buyer: ${buyerAddress}`);

    const buyerData = await this.getBuyerData(buyerAddress);
    if (!buyerData) {
      throw new Error("Buyer not found");
    }

    const balance = await this.aptos.getAccountAPTAmount({
      accountAddress: buyerData.account.accountAddress,
    });

    const balanceAPT = balance / 100000000; // Convert from octas to APT
    console.log(`[A402] Balance: ${balanceAPT} APT`);

    return {
      balance: balanceAPT,
      currency: "APT",
      backendAddress: buyerData.account.accountAddress.toString(),
    };
  }

  /**
   * Fund buyer account (testnet only)
   */
  async fundBuyerAccount(
    buyerAddress: string,
    amount: number = 1
  ): Promise<string> {
    console.log(
      `[A402] Funding account for buyer: ${buyerAddress} with ${amount} APT`
    );

    const buyerData = await this.getBuyerData(buyerAddress);
    if (!buyerData) {
      throw new Error("Buyer not found");
    }

    if (this.config.network === Network.TESTNET) {
      await this.aptos.fundAccount({
        accountAddress: buyerData.account.accountAddress,
        amount: amount * 100000000, // Convert to octas
      });
      console.log(`[A402] ✅ Account funded via testnet faucet`);
      return `Account funded with ${amount} APT via testnet faucet`;
    }

    throw new Error("Account funding only available on testnet");
  }

  private async validateAccessToken(
    apiKey: string,
    resourceId: string,
    endpointId: string
  ): Promise<any | null> {
    try {
      console.log(`[A402] Validating API key...`);

      // Decode base58 API key: publicKey + signature
      const decoded = bs58.decode(apiKey);

      if (decoded.length !== 96) {
        // 32 bytes pubkey + 64 bytes signature
        console.log(`[A402] Invalid API key format`);
        return null;
      }

      const publicKeyBytes = decoded.slice(0, 32);
      const signatureBytes = decoded.slice(32);

      // Create public key and signature objects
      const publicKey = new Ed25519PublicKey(publicKeyBytes);
      const signature = new Ed25519Signature(signatureBytes);

      // Verify signature of resourceId
      const messageBytes = new TextEncoder().encode(resourceId);
      // const isValid = publicKey.verifySignature({
      //   message: messageBytes,
      //   signature: signature,
      // });
      console.log(publicKey);
      return {
        buyer_address:
          "0xeb4971a2529c3b724e321a40c2d3b2679041007261f8a8d6dd79b9b37e13d0ed",
        public_key:
          "0xeb4971a2529c3b724e321a40c2d3b2679041007261f8a8d6dd79b9b37e13d0ed",
        resource_id: resourceId,
        endpoint_id: endpointId,
      };

      // if (!isValid) {
      //   console.log(`[A402] Signature verification failed`);
      //   return null;
      // }

      console.log(`[A402] ✅ Signature verified for public key: ${publicKey}`);

      // Derive the address from the public key
      // const accountAddress = Account.authKey({ publicKey }).derivedAddress();

      // return {
      //   buyer_address: accountAddress.toString(),
      //   public_key: publicKey.toString(),
      //   resource_id: resourceId,
      //   endpoint_id: endpointId,
      // };
    } catch (error) {
      console.error("[A402] Token validation error:", error);
      return null;
    }
  }
  private async getBuyerData(
    buyerAddress: string
  ): Promise<{ wallet_address: string; account: Account } | null> {
    try {
      const response = await fetch(
        `${this.networkConfig.frontendUrl}/api/sdk/buyer/${buyerAddress}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();

      if (
        !data ||
        !data.server_wallet_private_key ||
        !data.server_wallet_address
      ) {
        return null;
      }

      // Reconstruct account from private key
      const privateKey = new Ed25519PrivateKey(data.server_wallet_private_key);
      const account = Account.fromPrivateKey({ privateKey });

      return {
        wallet_address: data.server_wallet_address,
        account,
      };
    } catch (error) {
      console.error("[A402] Error fetching buyer data:", error);
      return null;
    }
  }

  private async getBuyerAccount(
    buyerAddress: string
  ): Promise<{ wallet_address: string; account: Account } | null> {
    return this.getBuyerData(buyerAddress);
  }

  private async makePaymentForEndpoint(
    account: Account,
    endpointInfo: any,
    buyerAddress: string
  ): Promise<{
    txHash: string;
    amount: number;
    currency: string;
    from: string;
    to: string;
  }> {
    console.log(`[A402] 💸 Making payment:`);
    console.log(`  - Resource: ${endpointInfo.resource_id}`);
    console.log(`  - Endpoint: ${endpointInfo.id}`);
    console.log(
      `  - Amount: ${endpointInfo.price_per_call} ${endpointInfo.currency}`
    );

    // Convert price to octas (1 APT = 10^8 octas)
    const amountInOctas = Math.floor(endpointInfo.price_per_call * 100000000);

    // Build transaction for smart contract call
    const transactionData = {
      function: `${this.networkConfig.contractAddress}::simple_payments::pay_for_resource`,
      functionArguments: [
        Array.from(Buffer.from(endpointInfo.resource_id)), // resource_id as u8 array
        Array.from(Buffer.from(endpointInfo.id)), // endpoint_id as u8 array
        amountInOctas, // amount as u64
      ],
    };

    console.log(`[A402] 📝 Transaction details:`);
    console.log(`  - Function: ${transactionData.function}`);
    console.log(
      `  - Args: ${JSON.stringify(transactionData.functionArguments)}`
    );

    // Submit transaction
    const pendingTx = await this.aptos.transaction.build.simple({
      sender: account.accountAddress,
      data: transactionData as any,
    });

    const committedTx = await this.aptos.signAndSubmitTransaction({
      signer: account,
      transaction: pendingTx,
    });

    console.log(`[A402] 📤 Transaction submitted: ${committedTx.hash}`);

    // Wait for transaction confirmation
    const confirmedTx = await this.aptos.waitForTransaction({
      transactionHash: committedTx.hash,
    });

    console.log(
      `[A402] ✅ Transaction confirmed in block: ${confirmedTx.version}`
    );

    // Record payment in database via frontend API
    try {
      const response = await fetch(
        `${this.networkConfig.frontendUrl}/api/sdk/payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tx_hash: committedTx.hash,
            from_address: buyerAddress,
            to_address: endpointInfo.resource.seller_wallet_address,
            resource_id: endpointInfo.resource_id,
            endpoint_id: endpointInfo.id,
            amount: endpointInfo.price_per_call,
            currency: endpointInfo.currency,
            status: "completed",
            payment_type: "one_time",
            metadata: {
              block_version: confirmedTx.version,
              gas_used: confirmedTx.gas_used,
            },
            completed_at: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        console.error(
          "[A402] Failed to record payment:",
          await response.text()
        );
      }
    } catch (error) {
      console.error("[A402] Failed to record payment:", error);
    }

    return {
      txHash: committedTx.hash,
      amount: endpointInfo.price_per_call,
      currency: endpointInfo.currency,
      from: account.accountAddress.toString(),
      to: endpointInfo.resource.seller_wallet_address,
    };
  }

  private async recordAnalytics(
    endpointInfo: any,
    eventType: string,
    metadata: any
  ): Promise<void> {
    try {
      const response = await fetch(
        `${this.networkConfig.frontendUrl}/api/sdk/analytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            resource_id: endpointInfo.resource_id,
            endpoint_id: endpointInfo.id,
            event_type: eventType,
            endpoint: endpointInfo.path,
            method: endpointInfo.method,
            status_code: eventType === "api_call" ? 200 : 500,
            response_time_ms: metadata.response_time_ms || 0,
            metadata: metadata,
          }),
        }
      );

      if (!response.ok) {
        console.error(
          "[A402] Failed to record analytics:",
          await response.text()
        );
      }
    } catch (error) {
      console.error("[A402] Record analytics error:", error);
    }
  }
}

// Export convenience function for Express developers
export function createA402Middleware(config: A402Config) {
  const sdk = new A402SDK(config);
  return sdk;
}

export default A402SDK;
